<template>
	<v-container fluid>
		<!-- add/edit dialog -->
		<v-dialog
			persistent
			v-model="dialog"
			max-width="400"
			content-class="rounded-medium"
		>
			<v-form ref="form" v-model="valid" @submit.prevent="submit()" :disabled="submitLoading">
				<v-card>
					<v-card-title class="pb-2">
						{{ userId ? $t('UsersPage.edit-user') : $t('UsersPage.create-user') }}
					</v-card-title>
					<div class="dialog-divider"/>
					<v-card-text class="pt-4 pb-0">
						<v-row dense class="my-0">
							<!-- user name -->
							<v-col cols="12">
								<v-text-field
									v-model="user.userName"
									:label="$t('UsersPage.username')"
									outlined
									dense
									:disabled="user.is_admin"
									:rules="user.is_admin ? [] : [rules.required, uniqueRule, rules.noBlankspace, rules.username]"
								></v-text-field>
							</v-col>

							<!-- password -->
							<v-col cols="12">
								<v-text-field
									v-model="user.password"
									:label="$t('UsersPage.password')"
									:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
									:type="showPassword ? 'text' : 'password'"
									outlined
									dense
									:rules="userId && !user.password ? undefined : [rules.required, rules.password]"
									@click:append="showPassword = !showPassword"
								></v-text-field>
							</v-col>
						</v-row>
					</v-card-text>

					<v-card-actions>
						<v-btn
							color="success"
							text
							:loading="submitLoading"
							type="submit"
							:disabled="!valid"
						>
							{{$t('UsersPage.save')}}
						</v-btn>
						<v-btn
							color="danger-color"
							text
							:disabled="submitLoading"
							@click="dialog = false"
						>
							{{$t('UsersPage.cancel')}}
						</v-btn>
						<v-spacer></v-spacer>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>
		<v-dialog
			v-model="editTimeZoneDialog"
			max-width="480"
			persistent
			content-class="rounded-medium"

		>
			<v-form v-model="valid" ref="form" :disabled="submitLoading" @submit.prevent="submit()">
				<v-card>
					<v-card-title class="pb-2">
						{{ $t('UsersPage.edit-time-zone') }}
					</v-card-title>
					<div class="dialog-divider"/>
					<v-card-text class="pt-4 pb-0">
						<v-row dense class="my-0">
							<!-- time zone -->
							<v-col cols="12">
								<v-autocomplete
									v-model="timezone"
									dense
									outlined
									:label="$t('UsersPage.time-zone')"
									item-text="timezone"
									item-value="timezone"
									:messages="[currentTimezone !== timezone?$t('UsersPage.your-current-timezone')+ currentTimezone+ $t('UsersPage.click-for-choose'): '']"
									:rules="[v => Boolean(getTimeZones.find(t => t.timezone === v))]"
									:items="getTimeZones"
								>
									<template v-slot:item="{item}">
										<div class="d-flex justify-space-between w-100">
											<span>{{item.detailedTimeZone}}</span>
											<span dir="ltr" class="ms-1 text-subtitle-2">
												{{ 														
													new Date(item.dateTime).toLocaleDateString('en-US', { 
														year: 'numeric', 
														month: '2-digit', 
														day: '2-digit' 
													})
												}}
												{{
													new Date(item.dateTime).toLocaleTimeString('en-US', {
															hour: '2-digit',
															minute: '2-digit',
															hour12: true
														})
												}}
											</span>
										</div>
									</template>
									<template v-slot:selection="{item}">
										<div class="d-flex justify-space-between w-100">
											<span>{{item.detailedTimeZone}}</span>
											<span dir="ltr" class="ms-1 text-subtitle-2">
												{{ 
																										
													new Date(item.dateTime).toLocaleDateString('en-US', { 
														year: 'numeric', 
														month: '2-digit', 
														day: '2-digit' 
													})
												}}
												{{
													new Date(item.dateTime).toLocaleTimeString('en-US', {
															hour: '2-digit',
															minute: '2-digit',
															hour12: true
														})
												}}
											</span>
										</div>
									</template>
									<template v-slot:message="{message}">
										<p class="red--text mt-1 mb-1" :style="'cursor:pointer'" @click="timezone = currentTimezone">
											<span>{{message}}</span>
										</p>
									</template>
								</v-autocomplete>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-btn
							color="success"
							text
							:loading="submitLoading"
							type="submit"
							:disabled="!valid"
						>
							{{$t('UsersPage.save')}}
						</v-btn>
						<v-btn
							color="danger-color"
							text
							:disabled="submitLoading"
							@click="editTimeZoneDialog = false"
						>
							{{$t('UsersPage.cancel')}}
						</v-btn>
						<v-spacer></v-spacer>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>
		<!-- delete dialog -->
		<bee-dialog
			v-model="deleteDialog"
			max-width="350"
			:persistent="true"
			:title="$t('UsersPage.delete-user')"
			:message="$t('UsersPage.are-you-sure-you-want-to-delete-the-user-$', { username: user.userName })"
			@click:confirm="confirmDeleteItem"
			@click:close="userId = null; deleteDialog = false;"
		></bee-dialog>

		<!-- change users state -->
		<bee-dialog
			v-model="changeStatusDialog"
			max-width="400"
			:title="$t('UsersPage.change-account-state')"
			:persistent="true"
			:loadingConfirm="submitLoading"
			@click:confirm="confirmChangeStatus"
			@click:close="changeStatusDialog = false; restUserData()"
		>
			<template v-slot:message>
				{{$t('UsersPage.are-you-sure-you-want-to')}}
				<span :class="user.isActive ? 'redDarken1--text': 'addingColor--text'">
					{{`${!user.isActive ? $t('UsersPage.activate') : $t('UsersPage.deactivate')}`}}
				</span>
				{{$t('UsersPage.account', { username: userId ? getUserById(userId).user_name : ''})}}
			</template>
		</bee-dialog>

		<!-- user files dialog -->
		<v-dialog
			v-model="userFilesDialog"
			persistent
			max-width="600"
			content-class="rounded-medium"
		>
			<v-card flat>
				<v-card-title>
					{{`${$t('UsersPage.files-which')} ${user.userName} ${$t('UsersPage.has-access-to-it')}`}}
				</v-card-title>
				<v-card-text class="py-0">
					<bee-handy-table
						:items="permissionsLoading ? [] : userPermissions"
						:headers="[
							{text: $t('UsersPage.temp.financial-file'), value: 'financialFileName', align: 'center'},
							{text: $t('UsersPage.temp.permission'), value: 'permissionType', align: 'center'},
						]"
						:items-per-page="-1"
						hide-default-footer
						:loading="permissionsLoading"
					></bee-handy-table>
				</v-card-text>
				<v-card-actions>
					<v-btn
						color="success"
						text
						@click="userId = null; userFilesDialog = false; restUserData();"
					>
						{{$t('UsersPage.ok')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- confirm changing password dialog -->
		<bee-dialog
			v-model="confirmChangingPasswordDialog"
			:persistent="true"
			:title="$t('UsersPage.security-alert')"
			:message="$t('UsersPage.changing-your-password-required-logout-then-you-can-login')"
			:confirm-btn-content="$t('UsersPage.confirm')"
			:loadingConfirm="confirmChangingPasswordLoading"
			@click:confirm="submit(true);"
			@click:close="confirmChangingPasswordDialog = false;"
		></bee-dialog>

		<!-- user permission dialog -->
		<v-dialog
			v-model="userPermissionDialog"
			persistent
			max-width="500"
			content-class="rounded-medium"
			>
			<v-card 
				v-if="permissionsDialogLoading"
				height="250"
				class="d-flex align-center justify-center"
			>
				<div class="spinner" style="border-radius: 50%"></div>
			</v-card>
			<v-card v-if="!permissionsDialogLoading">
				<v-card-title class="d-flex">
					
					<div class="mx-1">
						{{$t('user permission')}}
					</div>
					<v-spacer></v-spacer>
					<div>
						<v-btn 
							depressed
							color="success white--text"
							class="ms-2 d-flex text-capitalize"
							text
							small
							@click="permissionTypes.push({ type: null, financialFiles: [] } );"
							:loading="permissionsLoading"
						>
							<span class="mx-2">{{$t('UsersPage.addPermission')}}</span>
							<v-icon>mdi-plus-circle-outline</v-icon>
						</v-btn>
					</div>
					
				</v-card-title>
				<v-card-text class="py-1">
					<p v-if="permissionTypes.length === 0">{{$t('UsersPage.NoAddedPermission')}}</p>
					<v-form>
						<v-row 
							class="d-flex"
							v-for="(permissionType, index) in permissionTypes"
							:key="index"
							dense
						>
							<v-col cols="12" sm="5">
								<v-autocomplete
									outlined 
									dense
									:items="filteredPermissions(index)"
									item-value="id" 
									item-text="value"
									hide-details="auto"
									:label="$t('inputs.permission')"
									v-model="permissionTypes[index].type"
									:rules="[v => !!v || $t('validation.required')]"
								></v-autocomplete>
							</v-col>
							<v-spacer></v-spacer>
							<v-col cols="12" sm="5">
								<v-autocomplete
									outlined 
									dense
									hide-details="auto"
									:items="filteredFinancialFiles(index)"
									item-value="id"
									item-text="company_name"
									multiple
									:label="$t('financial-file')"
									v-model="permissionTypes[index].financialFiles"
									:rules="[v => v.length > 0 || $t('validation.required')]"
								></v-autocomplete>
							</v-col>
							<v-col cols="12" sm="auto">
								<v-btn
									depressed
									color="red white--text"
									class="ms-2"
									text
									width="35"
									min-width="35"
									height="35"
									@click="permissionTypes.splice(index, 1)"
								>
									<v-icon>mdi-delete</v-icon>
								</v-btn>
							</v-col>
							<v-col cols="12">
								<v-row 
									v-if="permissionType.type === 3 && permissionType.financialFiles?.length"
									dense  
									class="mt-3"
								>
									<v-col cols="12" lg="6" class="d-flex align-center">
										<span>
											{{ $t('UsersPage.select-the-warehouses') }}
										</span>
									</v-col>
									<v-col cols="12" lg="6">
										<v-menu-autocomplete
											v-model="permissionTypes[index].warehouseIds"
											:items="warehouses.map(({ id, name, financial_file_id }) => ({ id, name, financial_file_id }))"
											:treeItems="warehousesTree"
											item-text="name"
											item-value="id"
											:multiple="true"
											:outlined="true"
											:dense="true"
											:clearable="true"
											:hideDetails="true"
											:label="$t('inputs.warehouses')"
											:rules="[v => v.length > 0 || $t('validation.required')]"
										>
										</v-menu-autocomplete>
									</v-col>
								</v-row>
							</v-col>
							<v-col cols="12" sm="auto" v-if="useUnPostingPermission">
								<v-checkbox 
									dense
									outlined
									hide-details
									:label="$t('UsersPage.cancel-deportation')"
									@change="postPermission()"
									v-model="permissionTypes[index].canUnPost"
								></v-checkbox>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
				<v-card-actions>
					<v-btn
						color="success"
						text
						:loading="submitLoading"
						:disabled="permissionTypes.some(pt =>
							!pt.type ||
							!pt.financialFiles?.length ||
							(pt.type === userPermission.warehouseKeeper && !pt.warehouseIds?.length)
						)"
						type="submit"
						@click="submitUserPermissions()"
					>
						{{$t('UsersPage.save')}}
					</v-btn>
					<v-btn
						color="danger-color"
						text
						:disabled="submitLoading"
						@click="userPermissionDialog = false"
					>
						{{$t('UsersPage.cancel')}}
					</v-btn>
					<v-spacer></v-spacer>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- title and add button -->
		<v-row class="mb-2" dense align="center">
			<v-col cols="auto">
				<slot name="breadcrumbs"></slot>
			</v-col>
			<v-spacer></v-spacer>
			<v-col cols="auto">
				<v-btn
					color="success white--text"
					@click="dialog = true"
				>
					<v-icon size="25" class="me-1">mdi-account-plus</v-icon>
					{{$t('UsersPage.create-user')}}
				</v-btn>
			</v-col>
		</v-row>

		<!-- table -->
		<!-- FIXME use users:items="users" -->

		<bee-handy-table
			:headers="headers"
			:items="loading ? [] : users"
			:items-per-page="20"
			pagination-on-scroll
			dense
			fixed-header
			hide-shown-rows-text="auto"
			:data-table-props="{ disableSort: true }"
			:height="$vuetify.breakpoint.height - 150"
			:shown-rows-text="$t('components.shown-rows')"
			:loading="loading"
		>
			<!-- active state -->
			<template v-slot:item.isActive="{ item }">
				<v-chip small dark :color="item.is_active ? 'success' : 'grey darken-2'">
					<span class="me-1">
						{{item.is_active ? $t('UsersPage.active') : $t('UsersPage.unactive')}}
					</span>
					<v-icon size="22">
						{{ item.is_active ? 'mdi-account-check' : 'mdi-account-cancel' }}
					</v-icon>
				</v-chip>
			</template>

			<!-- permission -->
			<template v-slot:item.canCreateFinancialFile="{ item }">
				<span :class="item.can_create_financial_file ? 'addingColor--text' : 'grey--text'">
					{{ item.can_create_financial_file ? $t('UsersPage.allowed') : $t('UsersPage.not-allowed')}}
				</span>
			</template>

			<!-- actions -->
			<template v-slot:item.actions="{ item }">

				<!-- User Permission -->
				<v-tooltip top>
					<template v-slot:activator="{ attr, on }">
						<v-btn
							height="34"
							width="38"
							min-width="38"
							class="px-0 me-1"
							color="orange white--text"
							:disabled="item.is_admin"
							@click="userPermissionDialog = true; userId = item.id; getUserPermissionsData()"
							v-bind="attr"
							v-on="on"
						>
							<v-icon>mdi-account-cog</v-icon>
						</v-btn>
					</template>
					{{$t('users permissions')}}
				</v-tooltip>

				<!-- change-state -->
				<v-tooltip top>
					<template v-slot:activator="{ attr, on }">
						<v-btn
							height="34"
							width="38"
							min-width="38"
							:disabled="item.is_admin"
							class="px-0 me-1"
							color="info-color white--text"
							@click="userId = item.id; changeStatusDialog = true"
							v-bind="attr"
							v-on="on"
						>
							<v-icon>mdi-account-wrench</v-icon>
						</v-btn>
					</template>
					{{$t('tooltip.change-state')}}
				</v-tooltip>
				
				<!-- edit -->
				<v-tooltip top>
					<template v-slot:activator="{ attr, on }">
						<v-btn
							height="34"
							width="38"
							min-width="38"
							class="px-0 me-1"
							color="edit-color white--text"
							@click="userId = item.id; dialog = true"
							v-bind="attr"
							v-on="on"
						>
							<v-icon>mdi-pencil</v-icon>
						</v-btn>
					</template>
					{{$t('tooltip.edit')}}
				</v-tooltip>
				<v-tooltip top>
					<template v-slot:activator="{attr, on}">
						<v-btn height="34"
							width="38"
							min-width="38"
							class="px-0 me-1"
							color="red lighten-1 white--text"
							@click="userId = item.id; editTimeZoneDialog=true"
							v-bind="attr"
							v-on="on"
						>
							<v-icon>
								mdi-account-clock
							</v-icon>
						</v-btn>
					</template>
					{{ $t('UsersPage.edit-time-zone') }}
				</v-tooltip>
				<!-- delete -->
				<v-tooltip top>
					<template v-slot:activator="{ attr, on }">
						<v-btn
							height="34"
							width="38"
							min-width="38"
							:disabled="item.is_admin"
							class="px-0 me-1"
							color="red lighten-1 white--text"
							@click="userId = item.id; deleteDialog = true;"
							v-bind="attr"
							v-on="on"
						>
							<v-icon>mdi-delete</v-icon>
						</v-btn>
					</template>
					{{$t('tooltip.delete')}}
				</v-tooltip>
				
			</template>
		</bee-handy-table>
	</v-container>
</template>
<script>
import VMenuAutocomplete from '@/components/VMenuAutocomplete.vue';
import { userPermission, permissionNames, rolePermissions } from '../../../helpers/enums'
import rules from "@/validation";
import { mapState, mapGetters } from 'vuex';
import { useWarehousesPermissions, useUnPostingPermission } from '@/configs/global';
import timeZones from '@/helpers/timeZones'
import ls from '../../../settings/localStorageExtension';
export default {
	components: {
		VMenuAutocomplete
	},
	data: () => ({
		userPermission,
		rules: rules.rules,

		loading: false,
		submitLoading: false,
		permissionsLoading: false,
		confirmChangingPasswordLoading: false,

		deleteDialog: false,
		changeStatusDialog: false,
		confirmChangingPasswordDialog: false,

		userFilesDialog: false,
		userPermissions: [],

		permissionTypes: [ 
			{ 
				type: null, 
				financialFiles: [], 
				canPost: true,
				canUnPost: false,
				warehouseIds: []
			} 
		],
		ls,
		dialog: false,
		userId: null,
		showPassword: false,
		user: {
			id: null,
			userName: null,
			isActive: null,
			canCreateFinancialFile: null,
			password: null,
		},
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		currentTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		userPermissionDialog: false,
		...rules,

		useUnPostingPermission,
		permissionsDialogLoading: false,
		valid: true,
		// FIXME delete usersss
		usersss: [
			{
				id: '1ed9e61c-e28c-4b3c-979e-0636dcafc25f',
				user_name: 'SystemAdmin',
				is_active: true,
				is_admin: true,
				can_create_financial_file: true,
				permissions: [],
				password: null,
			},
			{
				id: '44a89500-a41d-45ad-a3a1-aec3952345e4',
				user_name: 'Accountant',
				is_active: true,
				is_admin: false,
				can_create_financial_file: false,
				permissions: [],
				password: null,
			},
			{
				id: '81cb3bd0-4a30-49e6-83fe-eda7c21d7f6e',
				user_name: 'TestAccountant',
				is_active: true,
				is_admin: false,
				can_create_financial_file: false,
				permissions: [],
				password: null,
			},
		],
		editTimeZoneDialog: false
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (this.userId) {
					const user = this.getUserById(this.userId);
					this.user.id = user.id;
					this.user.userName = user.user_name;
					this.user.isActive = user.is_active;
					this.user.canCreateFinancialFile = user.can_create_financial_file;
					this.user.permissions = user.permissions;
					this.user.password = user.password;
				}
			} else {
				this.$refs.form.reset();
				this.userId = null;
			}

		},
		editTimeZoneDialog(val) {
			if(val) {
				if(this.userId) {
					const user = this.getUserById(this.userId);
					this.user.id = user.id;
					this.timezone = user.time_zone;
				} else {
					this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
				}
			} else {
				this.$refs.form.reset();
			}
		},
		timezone() {
			ls.setTimezoneAlertStatus(null)
		},
		changeStatusDialog(val) {
			if (!val) { 
				this.userId = null; 
			} else {
				const user = this.getUserById(this.userId);
				this.user.isActive = user.is_active
			}
		},
	},

	computed: {
		getTimeZones() {
			let timezones = timeZones();

			timezones.push({timezone: this.$t('UsersPage.default-time-zone')})
			return timezones;
		},
		warehousesTree() {
			const financialFileIds = this.permissionTypes[0].financialFiles;
			return this.financialFiles.filter(({ id }) => financialFileIds.includes(id)).map(({ id, company_name }) => ({
				id,
				name: company_name,
				children: this.warehouses.filter(({ financial_file_id }) => financial_file_id === id)
			}));
		},

		
		...mapState({
			users: state => state.users.users,
			financialFiles: state => state.financialFiles.financial_files,
			roles: state => state.roles.roles,
			currentFinancialFile: state => state.financialFiles.current_financial_file,
			warehouses: state => state.warehouses.warehouses
		}),
		...mapGetters({
			current_user_name: 'auth/username',
			getUserById: 'users/getUserById',
			getRoleById: 'roles/getRoleById'
		}),
		headers(){
			return [
				{
					text: this.$t('UsersPage.headers.username'),
					value: 'user_name',
					name: 'userName',
					type: 'string',
					width: '25%',
					align: 'center'
				},
				/* {
					text: this.$t('UsersPage.headers.permission-to-create-a-file'),
					value: 'can_create_financial_file',
					name: 'canCreateFinancialFile',
					type: '',
					width: '20%',
					align: 'center'
				}, */
				{
					text: this.$t('UsersPage.headers.account-state'),
					value: 'is_active',
					name: 'isActive',
					type: '',
					width: '25%',
					align: 'center'
				},
				{
					text: this.$t('UsersPage.headers.actions'),
					value: 'actions',
					name: 'actions',
					type: '',
					width: '30%',
					align: 'center'
				}
			]
		},
		Permissions(){
			const items = [
				{ id: userPermission.viewer, value: this.$t('UsersPage.permissions.viewer') },
				{ id: userPermission.accountant, value: this.$t('UsersPage.permissions.accountant') },
				{ id: userPermission.advanceAccountant, value: this.$t('UsersPage.permissions.advancedAccountant') },
				{ id: userPermission.treasurer, value: this.$t('UsersPage.permissions.treasurer') }
			];
			if (useWarehousesPermissions) {
				items.push(
					{ id: userPermission.warehouseKeeper, value: this.$t('UsersPage.permissions.warehouseKeeper') },
					{ id: userPermission.warehouseManger, value: this.$t('UsersPage.permissions.warehouseManager') }
				)
			}
			return items;
		},
		unselectedUsers(){
			return this.users.filter(c => !c.is_admin).map(item => ({
				...item,
				disabled: this.userPermissions.map(c => c.userId).includes(item.id)
			}))
		},
	},
	methods: {
		// fetching data
		timeZones,
		fetchUsers(isFirstLoad = false) {
			this.loading = true;
			return this.$store.dispatch('users/fetchAll').finally(() => {
				if (!isFirstLoad) this.loading = false;
			})
		},
		
		// submit functions
		submit(isNeedToLogout = false) {
			if (!this.$refs.form.validate()) return;
			
			if (
				!isNeedToLogout &&
				this.userId &&
				this.user.userName == this.current_user_name &&
				this.user.password
			) {
				this.confirmChangingPasswordDialog = true;
				return
			}
			
			if (!isNeedToLogout) this.submitLoading = true;
			else this.confirmChangingPasswordLoading = true;
			
			const actionsName = !this.userId ? 'create' : 'update';
			this.$store.dispatch(`users/${actionsName}`,{
				id: this.user.id,
				userName: this.user.userName,
				password: this.user.password || null,
				timezone: this.timezone !== this.$t('UsersPage.default-time-zone')? this.timezone : null,
				canCreateFinancialFile: this.user.canCreateFinancialFile,
				// roleId: ,
				// isActive: , 
				// isAdmin:
			})
			.then(() => {
				this.dialog = false;
				this.editTimeZoneDialog = false;
				if (!isNeedToLogout) {
					this.fetchUsers();
				} else {
					this.$store.dispatch('auth/logout').then(() => {
						this.$router.push({ name: 'login' })
							.then(() => { this.$router.go(0); /* Force refresh */ });
					})
				}
			}).finally(() => {
				this.submitLoading = false;
				
			})
		},
		confirmChangeStatus(){
			this.submitLoading = true;
			this.$store.dispatch('users/changeStatus', {
				id: this.userId,
			}).then(() => {
				this.fetchUsers();
				this.restUserData();
				this.changeStatusDialog = false;
			}).finally(() => {
				this.submitLoading = false;
			})
		},
		confirmDeleteItem() {
			this.submitLoading = true;
			this.$store.dispatch('users/delete', this.userId).then(() => {
				this.fetchUsers();
				this.userId = false;
				this.deleteDialog = false;
			}).finally(() => {
				this.submitLoading = false;
			})
		},

		// helpers
		uniqueRule(value) {
			if (
				value &&
				this.users.length &&
				this.users.map(c => c.user_name.toLowerCase())
					.filter(c => this.userId ? c !== this.getUserById(this.userId).user_name.toLowerCase() : true)
					.includes(value.toLowerCase())
			)
				return this.$t('UsersPage.there-is-a-user-with-the-same-name')
			return true
		},

		restUserData() {
			this.user.id = null;
			this.user.userName = null;
			this.user.isActive = null;
			this.user.canCreateFinancialFile = false;
			this.user.password = null;
		},

		addUserPermission() {
			this.userPermissions.push({ userId: null, permissionType: null, warehouseIds: null });
			this.$nextTick(() => {
				this.$refs.username[this.userPermissions.length - 1].focus();
			});
		},
		
		filteredPermissions(index) {
			return this.Permissions.filter(item => 
				!this.permissionTypes.find((p, i) => i !== index && p.type === item.id)
			);
		},
			filteredFinancialFiles(index) {
			return this.financialFiles.filter(item => 
				!this.permissionTypes.find((p, i) => i !== index && p.financialFiles.includes(item.id))
			);
		},

		getUserPermissionsData() {
			this.permissionsDialogLoading = true
			this.$store.dispatch('users/fetchPermissions', {
				id: this.userId,
			})
			.then(data => {
				this.permissionTypes = data.map(permission => ({ 
					type: permission.role_type, 
					financialFiles:
						data.filter(c => c.role_type === permission.role_type).map(c => c.financial_file_id),

					canUnPost: data.filter(c => 
						c.role_type === permission.role_type &&
						c.role_permissions.some(c =>
							c.permissions_names === permissionNames.unPosting &&
							c.role_permissions === rolePermissions.action
						)
					).length > 0,
					warehouseIds: 
						data.flatMap(({ wrehouse_financial_fil_role_permissions }) => wrehouse_financial_fil_role_permissions.map(({ warehouse_id }) => warehouse_id))
				})).filter((c, index, arr) => arr.findIndex(e => e.type === c.type) === index); 
			}).finally(() => {
				this.permissionsDialogLoading = false
			})

		},
		submitUserPermissions() {
			this.submitLoading = true
			this.$store.dispatch('roles/updateUserPermissions', {
				permissions: this.permissionTypes.map(c => ({
					financial_file_ids: c.financialFiles,
					role_type: c.type,
					can_post: true,
					can_un_post: c.canUnPost,
					warehouse_ids: c.warehouseIds
				})),
				userId: this.userId
			}).then(() => {
				this.userPermissionDialog = false; 
			}).finally(() => { 
				this.submitLoading = false; 
			});
		},
		
		// the permission of canceling the post
		postPermission() {
			this.permissionTypes.canUnPost = !this.permissionTypes.canUnPost
		},
	},
	created() {

		this.$store.dispatch('warehouses/fetchAllWarehouses');
		this.$store.dispatch('roles/fetchAll');
		this.loading = true;

		Promise.all([
			!this.financialFiles.length ? this.$store.dispatch('financialFiles/fetchAll', {}) : null,
			!this.users.length ? this.fetchUsers(true) : null
		]).finally(() => {
			this.loading = false;
		})
	}
}
</script>

<style scoped>
	.spinner {
		width: 50px;
		height: 50px;
		border: 4px solid #ccc;
		border-top: 4px solid #39577d;
		animation: spin 1s linear infinite;
	}
	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
</style>